/**
 * Main terminal container component.
 * Manages multiple terminal tabs with per-tab split-pane support.
 *
 * @module components/terminal/TerminalContainer
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTerminalStore, createTerminalStore, TerminalStoreContext } from './useTerminalStore';
import type { TerminalStoreApi } from './useTerminalStore';
import { TerminalTabBar } from './TerminalTabBar';
import { TerminalPanel } from './TerminalPanel';
import type { TerminalContainerProps } from './types';

/**
 * Status bar showing connection info for active terminal
 */
function TerminalStatusBar({ connectionState }: { connectionState: string }) {
  return (
    <div className="flex-shrink-0 px-4 py-2 bg-[#161616] border-t border-[#2a2a2a]">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-400">
          {connectionState === 'disconnected' && 'Connecting to shell...'}
          {connectionState === 'connecting' && 'Establishing WebSocket connection...'}
          {connectionState === 'connected' && 'Interactive shell ready - type commands directly'}
          {connectionState === 'error' && 'Connection failed - check console for details'}
        </div>
      </div>
    </div>
  );
}

/**
 * Main terminal container with tabs and per-tab split support.
 * Wraps the inner component with an isolated store context.
 */
export function TerminalContainer(props: TerminalContainerProps) {
  const storeRef = useRef<TerminalStoreApi | null>(null);
  if (!storeRef.current) {
    storeRef.current = createTerminalStore();
  }

  return (
    <TerminalStoreContext.Provider value={storeRef.current}>
      <TerminalContainerInner {...props} />
    </TerminalStoreContext.Provider>
  );
}

/**
 * Inner terminal container that reads from the scoped store context.
 */
function TerminalContainerInner({
  sandboxId,
  containerName,
  sandboxGroupId,
  onClose,
  onConnectionError,
}: TerminalContainerProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  // Store state and actions — reads from the scoped context store
  const tabs = useTerminalStore((s) => s.tabs);
  const activeTabId = useTerminalStore((s) => s.activeTabId);
  const addTab = useTerminalStore((s) => s.addTab);
  const removeTab = useTerminalStore((s) => s.removeTab);
  const setActiveTab = useTerminalStore((s) => s.setActiveTab);
  const toggleSplit = useTerminalStore((s) => s.toggleSplit);
  const reset = useTerminalStore((s) => s.reset);
  const getActiveTerminalContent = useTerminalStore((s) => s.getActiveTerminalContent);

  // Initialize active tab on mount
  useEffect(() => {
    if (tabs.length > 0 && !activeTabId) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTabId, setActiveTab]);

  // Reset store on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Get active tab info
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const activeConnectionState = activeTab?.connectionState || 'disconnected';
  const activeTabHasSplit = activeTab?.split !== null;

  // Handlers
  const handleAddTab = useCallback(() => {
    addTab('bash');
  }, [addTab]);

  const handleCloseTab = useCallback(
    (tabId: string) => {
      removeTab(tabId);
    },
    [removeTab]
  );

  const handleSelectTab = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
    },
    [setActiveTab]
  );

  const handleToggleSplit = useCallback(() => {
    if (activeTabId) {
      toggleSplit(activeTabId);
    }
  }, [activeTabId, toggleSplit]);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const handleCopy = useCallback(async () => {
    const content = getActiveTerminalContent();
    if (content) {
      try {
        await navigator.clipboard.writeText(content);
        setCopyState('copied');
        setTimeout(() => setCopyState('idle'), 2000);
      } catch (err) {
        console.error('[TerminalContainer] Failed to copy:', err);
      }
    }
  }, [getActiveTerminalContent]);

  return (
    <div
      className={`adc-terminal-container flex flex-col bg-[#0a0a0a] overflow-hidden border border-[#2a2a2a] ${
        isFullscreen ? 'fixed inset-0 z-50 rounded-none' : 'h-full rounded-lg'
      }`}
    >
      {/* Tab Bar */}
      <TerminalTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        isFullscreen={isFullscreen}
        activeTabHasSplit={activeTabHasSplit}
        onAddTab={handleAddTab}
        onCloseTab={handleCloseTab}
        onSelectTab={handleSelectTab}
        onToggleSplit={handleToggleSplit}
        onToggleFullscreen={handleToggleFullscreen}
        onClose={onClose}
        onCopy={handleCopy}
        copyState={copyState}
      />

      {/* Terminal Panels - All tabs are always mounted, visibility controlled via CSS */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {tabs.map((tab) => {
          const isActiveTab = tab.id === activeTabId;
          const hasSplit = tab.split !== null;

          return (
            <div
              key={tab.id}
              className="absolute inset-0"
              style={{
                visibility: isActiveTab ? 'visible' : 'hidden',
                zIndex: isActiveTab ? 1 : 0,
              }}
            >
              {/* Container for main and split panels */}
              <div className="h-full w-full flex">
                {/* Main terminal panel */}
                <div
                  className="h-full"
                  style={{
                    width: hasSplit ? '50%' : '100%',
                    transition: 'width 0.15s ease-out',
                  }}
                >
                  <TerminalPanel
                    sandboxId={sandboxId}
                    containerName={containerName}
                    sandboxGroupId={sandboxGroupId}
                    tabId={tab.id}
                    panelType="main"
                    isActive={isActiveTab}
                    onConnectionError={onConnectionError}
                  />
                </div>

                {/* Split panel - only rendered when split exists */}
                {hasSplit && (
                  <>
                    {/* Divider */}
                    <div className="w-0.5 bg-[#2a2a2a] flex-shrink-0" />

                    {/* Split terminal panel */}
                    <div className="h-full" style={{ width: '50%' }}>
                      <TerminalPanel
                        sandboxId={sandboxId}
                        containerName={containerName}
                        sandboxGroupId={sandboxGroupId}
                        tabId={tab.id}
                        panelType="split"
                        isActive={isActiveTab}
                        onConnectionError={onConnectionError}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Status Bar */}
      <TerminalStatusBar connectionState={activeConnectionState} />
    </div>
  );
}
